import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { formatMoney } from "@/lib/constants";
import { Plus, Trash2, Edit, X } from "lucide-react";

const EMPTY = { material_id: "", quantity: "", unit_price: "", note: "" };

export const WorkLogMaterialsDialog = ({ log, title, onClose }) => {
  const [materials, setMaterials] = useState([]);
  const [usages, setUsages] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get(`/work-logs/${log.id}/materials`).then((r) => setUsages(r.data));
  useEffect(() => {
    api.get("/materials").then((r) => setMaterials(r.data));
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [log.id]);

  const selected = materials.find((m) => m.id === form.material_id);
  const total = (Number(form.quantity) || 0) * (Number(form.unit_price) || 0);
  const grand = usages.reduce((s, u) => s + u.total_cost, 0);

  const [priceTouched, setPriceTouched] = useState(false);

  const pickMaterial = (id) => {
    const m = materials.find((x) => x.id === id);
    setForm({ ...form, material_id: id, unit_price: priceTouched ? form.unit_price : m?.default_price ?? "" });
  };

  const save = async () => {
    if (!form.material_id || !(Number(form.quantity) > 0)) return toast.error("Malzeme ve miktar zorunludur");
    const body = { material_id: form.material_id, quantity: Number(form.quantity), unit_price: Number(form.unit_price) || 0,
                   note: (form.note || "").trim() };
    try {
      if (editingId) await api.put(`/material-usages/${editingId}`, body);
      else await api.post(`/work-logs/${log.id}/materials`, body);
      toast.success(editingId ? "Malzeme güncellendi" : "Malzeme eklendi");
      setForm(EMPTY); setEditingId(null); setPriceTouched(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail?.toString?.() || "Hata"); }
  };

  const edit = (u) => {
    setEditingId(u.id); setPriceTouched(true);
    setForm({ material_id: u.material_id, quantity: u.quantity, unit_price: u.unit_price, note: u.note || "" });
  };
  const remove = async (u) => {
    if (!window.confirm(`"${u.material_name}" kaydı silinsin mi?`)) return;
    try {
      await api.delete(`/material-usages/${u.id}`);
      toast.success("Silindi"); load();
    } catch (e) { toast.error("Silinemedi"); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="work-log-materials-dialog">
        <DialogHeader><DialogTitle>Kullanılan Malzemeler</DialogTitle></DialogHeader>
        <div className="text-sm text-slate-500 -mt-2">{title}</div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end p-4 bg-slate-50 rounded-md border border-slate-200">
          <div className="col-span-2">
            <Label className="text-xs">Malzeme *</Label>
            <Select value={form.material_id} onValueChange={pickMaterial}>
              <SelectTrigger data-testid="usage-material-select" className="bg-white"><SelectValue placeholder="Seçin" /></SelectTrigger>
              <SelectContent>
                {materials.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} ({m.unit})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Miktar {selected ? `(${selected.unit})` : ""} *</Label>
            <Input type="number" min="0" step="any" value={form.quantity} className="bg-white"
                   onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="usage-quantity-input" />
          </div>
          <div>
            <Label className="text-xs">Birim Fiyat (TL)</Label>
            <Input type="number" min="0" step="0.01" value={form.unit_price} className="bg-white"
                   onChange={(e) => { setPriceTouched(true); setForm({ ...form, unit_price: e.target.value }); }} data-testid="usage-price-input" />
          </div>
          <div>
            <Label className="text-xs">Toplam Maliyet</Label>
            <div className="h-9 flex items-center font-mono font-semibold text-slate-900" data-testid="usage-total-preview">
              {formatMoney(total)}
            </div>
          </div>
          <div className="col-span-2 md:col-span-5">
            <Label className="text-xs">Açıklama / Not (isteğe bağlı)</Label>
            <Textarea rows={2} maxLength={500} value={form.note} className="bg-white"
                      placeholder="Ör. teslim alan kişi, tedarikçi, kullanım yeri..."
                      onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="usage-note-input" />
          </div>
          <div className="col-span-2 md:col-span-5 flex justify-end gap-2">
            {editingId && (
              <Button variant="outline" size="sm" onClick={() => { setEditingId(null); setPriceTouched(false); setForm(EMPTY); }} data-testid="usage-cancel-edit-button">
                <X className="h-3.5 w-3.5 mr-1" /> Vazgeç
              </Button>
            )}
            <Button size="sm" onClick={save} className="bg-emerald-700 hover:bg-emerald-800" data-testid="usage-submit-button">
              <Plus className="h-3.5 w-3.5 mr-1" /> {editingId ? "Güncelle" : "Malzeme Ekle"}
            </Button>
          </div>
        </div>

        <div className="border border-slate-200 rounded-md overflow-hidden">
          <Table data-testid="usage-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Malzeme</TableHead>
                <TableHead className="text-right">Miktar</TableHead>
                <TableHead className="text-right">Birim Fiyat</TableHead>
                <TableHead className="text-right">Toplam</TableHead>
                <TableHead className="w-20 sticky right-0 bg-slate-50"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usages.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500">Henüz malzeme girilmedi.</TableCell></TableRow>
              )}
              {usages.map((u) => (
                <TableRow key={u.id} data-testid={`usage-row-${u.id}`}>
                  <TableCell>
                    <div className="font-medium">{u.material_name}</div>
                    {u.note && <div className="text-xs text-slate-500 mt-0.5 whitespace-pre-line" data-testid={`usage-note-${u.id}`}>{u.note}</div>}
                  </TableCell>
                  <TableCell className="text-right font-mono whitespace-nowrap">{u.quantity} {u.unit}</TableCell>
                  <TableCell className="text-right font-mono whitespace-nowrap">{formatMoney(u.unit_price)}</TableCell>
                  <TableCell className="text-right font-mono whitespace-nowrap">{formatMoney(u.total_cost)}</TableCell>
                  <TableCell className="sticky right-0 bg-white">
                    <div className="flex">
                      <Button variant="ghost" size="icon" onClick={() => edit(u)} data-testid={`edit-usage-${u.id}`}><Edit className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => remove(u)} data-testid={`delete-usage-${u.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <div className="flex justify-end text-sm text-slate-600">
          Malzeme Toplamı: <span className="ml-2 font-mono font-semibold text-slate-900" data-testid="usage-grand-total">{formatMoney(grand)}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
};
